import { getLocalTimeZone, today } from "@internationalized/date";
import { useState } from "react";
import type { DateValue } from "react-aria";

const now = today(getLocalTimeZone());
const oneYearAgo = now.subtract({ years: 1 });
const threeMonthsAgo = now.subtract({ months: 3 });

export type ExpenseReportDateRangeValue = {
	start: DateValue;
	end: DateValue;
} | null;

function checkDateUnavailable(
	date: DateValue,
	currentValue: ExpenseReportDateRangeValue
): boolean {
	if (date.compare(now) > 0) {
		return true;
	}
	if (date.compare(oneYearAgo) < 0) {
		return true;
	}
	if (currentValue?.start && !currentValue.end) {
		const maxEnd = currentValue.start.add({ years: 1 });
		if (date.compare(currentValue.start) < 0 || date.compare(maxEnd) > 0) {
			return true;
		}
	}
	if (currentValue?.end && !currentValue.start) {
		const minStart = currentValue.end.subtract({ years: 1 });
		if (date.compare(minStart) < 0 || date.compare(currentValue.end) > 0) {
			return true;
		}
	}
	return false;
}

function normalizeRange(
	start: DateValue,
	end: DateValue
): { start: DateValue; end: DateValue } {
	const adjustedEnd = end.compare(now) > 0 ? now : end;
	const maxRangeStart = adjustedEnd.subtract({ years: 1 });
	let adjustedStart = start.compare(maxRangeStart) < 0 ? maxRangeStart : start;
	if (adjustedStart.compare(adjustedEnd) > 0) {
		adjustedStart = adjustedEnd;
	}
	return { start: adjustedStart, end: adjustedEnd };
}

/**
 * Regras do intervalo de datas para relatórios de despesas:
 * - Datas futuras bloqueadas
 * - Datas anteriores a 1 ano bloqueadas
 * - Intervalo máximo de 1 ano entre início e fim
 * - Data inicial não pode ser maior que data fim (início <= fim)
 */
export function useExpenseReportDateRange(
	initialValue: ExpenseReportDateRangeValue = {
		start: threeMonthsAgo,
		end: now,
	}
) {
	const [value, setValue] = useState<ExpenseReportDateRangeValue>(initialValue);

	const isDateUnavailable = (date: DateValue): boolean =>
		checkDateUnavailable(date, value);

	const handleChange = (date: ExpenseReportDateRangeValue) => {
		if (!date) {
			setValue(null);
			return;
		}

		const { start, end } = date;

		if (start && end) {
			setValue(normalizeRange(start, end));
			return;
		}

		if (end && end.compare(now) > 0) {
			setValue({ ...date, end: now });
			return;
		}

		if (start && start.compare(now) > 0) {
			setValue({ start: now, end: now });
			return;
		}

		setValue(date);
	};

	return {
		value,
		setValue,
		isDateUnavailable,
		handleChange,
		maxValue: now,
		minValue: oneYearAgo,
	};
}
